import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useApp } from '@/context/AppContext';
import api, { errMsg } from '@/lib/api';
import { toast } from 'sonner';
import { Image as ImageIcon, Upload, Trash2, Loader2 } from 'lucide-react';

const readImage = (file, cb) => {
  if (!file) return;
  const ok = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'].includes(file.type);
  if (!ok) { toast.error('Use a PNG, JPG or SVG image.'); return; }
  if (file.size > 1200000) { toast.error('Image too large (max ~1.2MB).'); return; }
  const r = new FileReader();
  r.onload = () => cb(r.result);
  r.readAsDataURL(file);
};

// Dedicated, always-visible control to set the app logo. It updates the browser
// tab icon and the installed home-screen icon (composited on a light background
// so it never shows as a black square). Owner only.
export function AppLogoPanel() {
  const { settings, loadSettings, user } = useApp();
  const isOwner = user?.role === 'OWNER' || user?.owner_access;
  const [logo, setLogo] = useState(settings?.branding?.logo || '');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setLogo(settings?.branding?.logo || ''); setDirty(false); }, [settings]);

  if (!isOwner) return null;

  const onFile = (file) => readImage(file, (d) => { setLogo(d); setDirty(true); });
  const removeLogo = () => { setLogo(''); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const branding = { ...(settings?.branding || {}), logo };
      await api.put('/settings', { branding });
      await loadSettings();
      setDirty(false);
      toast.success(logo ? 'App logo updated' : 'App logo removed');
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="app-logo-panel">
      <div className="mb-1 flex items-center gap-2"><ImageIcon className="h-4 w-4" /><p className="text-sm font-semibold">App logo</p></div>
      <p className="mb-3 text-xs text-muted-foreground">Sets the browser tab icon and the installed home-screen icon. Use a square PNG for best results.</p>

      <div className="flex items-center gap-4">
        {/* Live icon preview on a light background — matches how the installed icon renders. */}
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-border bg-white p-1.5" data-testid="app-logo-preview">
          {logo
            ? <img src={logo} alt="App logo" className="h-full w-full object-contain" />
            : <span className="font-display text-2xl font-bold text-primary">S</span>}
        </div>
        <div className="flex flex-1 flex-wrap gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium hover:bg-secondary" data-testid="app-logo-upload-label">
            <Upload className="h-3.5 w-3.5" />{logo ? 'Change logo' : 'Upload logo'}
            <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="app-logo-upload" />
          </label>
          {logo && (
            <button onClick={removeLogo} className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-2 text-xs font-medium text-destructive hover:bg-destructive/10" data-testid="app-logo-remove">
              <Trash2 className="h-3.5 w-3.5" />Remove
            </button>
          )}
        </div>
      </div>

      <Button className="mt-4 h-11 w-full rounded-xl" onClick={save} disabled={saving || !dirty} data-testid="app-logo-save">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save app logo'}
      </Button>
    </div>
  );
}
